import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Center,
  Container,
  Spacer,
  Spinner,
  Stack,
} from '@cardboard-ui/react';
import { CantRespondDraftFormAlert } from '../EditForm/components/CantRespondDraftFormAlert';
import { CantRespondClosedFormAlert } from '../EditForm/components/CantRespondClosedFormAlert';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import { RespondForm_folderItem$key } from './__generated__/RespondForm_folderItem.graphql';
import { RespondForm_form$key } from './__generated__/RespondForm_form.graphql';
import { RespondQuestionCard } from './RespondQuestionCard';
import { t } from '@lingui/macro';
import { SuccessfullySubmittedFormAlert } from './SuccessfullySubmittedFormAlert';
import { useForm } from 'react-hook-form';
import {
  AnswerInput,
  RespondForm_updateResponse_Mutation,
} from './__generated__/RespondForm_updateResponse_Mutation.graphql';
import { RespondForm_submitResponse_Mutation } from './__generated__/RespondForm_submitResponse_Mutation.graphql';
import { RespondForm_ensureDraftResponse_Mutation } from './__generated__/RespondForm_ensureDraftResponse_Mutation.graphql';
import debounce from 'lodash/debounce';
import { useI18n } from 'utils/i18n';
import {
  RespondForm_Response$data,
  RespondForm_Response$key,
} from './__generated__/RespondForm_Response.graphql';
import { notEmpty } from 'utils/NotEmptyFilter';

const folderItemFragment = graphql`
  fragment RespondForm_folderItem on FolderItem {
    publishState
  }
`;

const fragment = graphql`
  fragment RespondForm_form on Form {
    id
    isClosed
    questions {
      id
      ...RespondQuestionCard_formQuestion
    }
  }
`;

const responseFragment = graphql`
  fragment RespondForm_Response on FormResponse {
    id
    state
    respondedAt
    state
    responder {
      ... on Member {
        name
      }
    }
    answers {
      id
      questionId
      value {
        __typename
        ... on CustomValue__String {
          text: value
        }
        ... on CustomValue__Numeric {
          number: value
        }
        ... on CustomValue__Checkboxes {
          checkboxes: value
        }
        ... on CustomValue__MultipleChoice {
          multipleChoice: value
        }
        ... on CustomValue__Date {
          date: value
        }
        ... on CustomValue__Time {
          time: value
        }
        ... on CustomValue__DateTime {
          dateTime: value
        }
        ... on CustomValue__PhoneNumber {
          phoneNumber: value
        }
        ... on CustomValue__Link {
          link: value
        }
        ... on CustomValue__Email {
          email: value
        }
      }
    }
  }
`;

const ensureDraftResponseMutation = graphql`
  mutation RespondForm_ensureDraftResponse_Mutation(
    $input: FormsEnsureDraftResponseMutationInput!
  ) {
    ensureDraftResponse(input: $input) {
      folderNode {
        ... on FolderItem {
          id
          canEdit: canRename {
            value
          }
        }
      }
      response {
        ...RespondForm_Response
        answers {
          id
          questionId
          value {
            __typename
            ... on CustomValue__String {
              text: value
            }
            ... on CustomValue__Numeric {
              number: value
            }
            ... on CustomValue__Checkboxes {
              checkboxes: value
            }
            ... on CustomValue__MultipleChoice {
              multipleChoice: value
            }
            ... on CustomValue__Date {
              date: value
            }
            ... on CustomValue__Time {
              time: value
            }
            ... on CustomValue__DateTime {
              dateTime: value
            }
            ... on CustomValue__PhoneNumber {
              phoneNumber: value
            }
            ... on CustomValue__Link {
              link: value
            }
            ... on CustomValue__Email {
              email: value
            }
          }
        }
      }
    }
  }
`;

const updateResponseMutation = graphql`
  mutation RespondForm_updateResponse_Mutation(
    $input: FormsUpdateResponseMutationInput!
  ) {
    updateResponse(input: $input) {
      response {
        ...RespondForm_Response
      }
    }
  }
`;

const submitResponseMutation = graphql`
  mutation RespondForm_submitResponse_Mutation(
    $input: FormsSubmitResponseMutationInput!
  ) {
    submitResponse(input: $input) {
      # form is returned to update Responses count indicator and data table
      form {
        id
        responses {
          edges {
            node {
              ...RespondForm_Response
            }
          }
          totalCount
        }
      }
      response {
        ...RespondForm_Response
      }
    }
  }
`;

interface RespondFormProps {
  folderItem: RespondForm_folderItem$key;
  form: RespondForm_form$key;
}

export const RespondForm = ({ folderItem, form: _form }: RespondFormProps) => {
  const { publishState } = useFragment(folderItemFragment, folderItem);
  const { id: formId, isClosed, questions } = useFragment(fragment, _form);
  const [canEdit, setCanEdit] = useState(false);
  const [responseKey, setResponseKey] =
    useState<RespondForm_Response$key | null>(null);
  const response = useFragment(responseFragment, responseKey);
  const [loadingAnswers, setLoadingAnswers] = useState<
    ReadonlyArray<AnswerInput>
  >([]);
  const isAnswerInFlight = loadingAnswers.length > 0;
  const { format } = useI18n();

  const form = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const isDraft = publishState === 'DRAFT';
  const isSubmitted = response?.state === 'SUBMITTED';
  const hasAnswers = hasNonEmptyAnswers(response);

  const [ensureDraftResponse, isEnsuringDraftResponse] =
    useMutation<RespondForm_ensureDraftResponse_Mutation>(
      ensureDraftResponseMutation,
    );

  const [updateResponse] = useMutation<RespondForm_updateResponse_Mutation>(
    updateResponseMutation,
  );

  const [submitResponse, isSubmitResponseLoading] =
    useMutation<RespondForm_submitResponse_Mutation>(submitResponseMutation);

  const saveAnswer = useCallback(
    debounce((newAnswer: AnswerInput) => {
      if (!response) return;

      setLoadingAnswers([newAnswer]);
      updateResponse({
        variables: {
          input: {
            responseId: response.id,
            answers: [newAnswer],
          },
        },
        onCompleted: (
          data: RespondForm_updateResponse_Mutation['response'],
        ) => {
          setLoadingAnswers([]);
          setResponseKey(data.updateResponse?.response || null);
        },
      });
    }, 350),
    [response],
  );

  function handleEnsureDraftResponse() {
    ensureDraftResponse({
      variables: {
        input: { formId },
      },
      onCompleted: (
        data: RespondForm_ensureDraftResponse_Mutation['response'],
      ) => {
        setCanEdit(!!data.ensureDraftResponse?.folderNode.canEdit?.value);
        const newResponse = data.ensureDraftResponse?.response || null;
        setResponseKey(newResponse);
        newResponse?.answers.forEach((answer) => {
          switch (answer.value?.__typename) {
            case 'CustomValue__String':
              form.setValue(answer.questionId, answer.value.text);
              break;
            case 'CustomValue__Numeric':
              form.setValue(answer.questionId, answer.value.number);
              break;
            case 'CustomValue__Checkboxes':
              form.setValue(answer.questionId, answer.value.checkboxes);
              break;
            case 'CustomValue__MultipleChoice':
              form.setValue(answer.questionId, answer.value.multipleChoice);
              break;
            case 'CustomValue__Date':
              form.setValue(answer.questionId, answer.value.date);
              break;
            case 'CustomValue__Time':
              form.setValue(answer.questionId, answer.value.time);
              break;
            case 'CustomValue__DateTime':
              form.setValue(
                `${answer.questionId}-date`,
                answer.value.dateTime
                  ? format(new Date(answer.value.dateTime), 'yyyy-MM-dd')
                  : '',
              );
              form.setValue(
                `${answer.questionId}-time`,
                answer.value.dateTime
                  ? format(new Date(answer.value.dateTime), 'HH:mm')
                  : '',
              );
              break;
            case 'CustomValue__PhoneNumber':
              form.setValue(answer.questionId, answer.value.phoneNumber);
              break;
            case 'CustomValue__Link':
              form.setValue(answer.questionId, answer.value.link);
              break;
            default:
              break;
          }
        });
      },
    });
  }

  function handleClearAllAnswers() {
    if (!response) return;

    const clearedAnswers = response.answers
      .map((answer) => {
        switch (answer.value?.__typename) {
          case 'CustomValue__String':
            form.setValue(answer.questionId, '');
            return {
              questionId: answer.questionId,
              stringValue: null,
            };
          case 'CustomValue__Numeric':
            form.setValue(answer.questionId, '');
            return {
              questionId: answer.questionId,
              numericValue: null,
            };
          case 'CustomValue__Checkboxes':
            form.setValue(answer.questionId, []);
            return {
              questionId: answer.questionId,
              checkboxesValue: null,
            };
          case 'CustomValue__MultipleChoice':
            form.setValue(answer.questionId, '');
            return {
              questionId: answer.questionId,
              multipleChoiceValue: null,
            };
          case 'CustomValue__Date':
            form.setValue(answer.questionId, '');
            return {
              questionId: answer.questionId,
              dateValue: null,
            };
          case 'CustomValue__Time':
            form.setValue(answer.questionId, '');
            return {
              questionId: answer.questionId,
              timeValue: null,
            };
          case 'CustomValue__DateTime':
            form.setValue(`${answer.questionId}-date`, '');
            form.setValue(`${answer.questionId}-time`, '');
            return {
              questionId: answer.questionId,
              dateTimeValue: null,
            };
          case 'CustomValue__PhoneNumber':
            form.setValue(answer.questionId, '');
            return {
              questionId: answer.questionId,
              phoneNumberValue: null,
            };
          case 'CustomValue__Link':
            form.setValue(answer.questionId, '');
            return {
              questionId: answer.questionId,
              linkValue: null,
            };
          default:
            return null;
        }
      })
      .filter(notEmpty);

    setLoadingAnswers(clearedAnswers);
    updateResponse({
      variables: {
        input: {
          responseId: response.id,
          answers: clearedAnswers,
        },
      },
      onCompleted: (data: RespondForm_updateResponse_Mutation['response']) => {
        setLoadingAnswers([]);
        setResponseKey(data.updateResponse?.response || null);
      },
    });
  }

  function handleSubmitResponse() {
    if (!response || loadingAnswers.length) return;

    submitResponse({
      variables: {
        input: {
          responseId: response.id,
        },
      },
      onCompleted: (data: RespondForm_submitResponse_Mutation['response']) => {
        setResponseKey(data.submitResponse?.response || null);
      },
    });
  }

  useEffect(() => {
    if (!isDraft && !isClosed) {
      handleEnsureDraftResponse();
    }
  }, []);

  return (
    <Container size="container.xl">
      {isEnsuringDraftResponse ? (
        <Center>
          <Spinner />
        </Center>
      ) : (
        <form onSubmit={form.handleSubmit(handleSubmitResponse)}>
          <Stack spacing={4}>
            {isSubmitted && (
              <SuccessfullySubmittedFormAlert
                onSuccess={() => {
                  form.reset();
                  handleEnsureDraftResponse();
                }}
              />
            )}
            {isDraft && <CantRespondDraftFormAlert canEdit={canEdit} />}
            {isClosed && <CantRespondClosedFormAlert />}
            {!isDraft &&
              !isClosed &&
              !isSubmitted &&
              questions.map((question) => (
                <RespondQuestionCard
                  key={question.id}
                  form={form}
                  question={question}
                  saveAnswer={saveAnswer}
                  isLoading={loadingAnswers.some(
                    (a) => a.questionId === question.id,
                  )}
                />
              ))}

            <ButtonGroup>
              <Button
                hidden={isClosed || isDraft || isSubmitted}
                isDisabled={!hasAnswers || isAnswerInFlight}
                onClick={handleClearAllAnswers}
              >
                {t`Clear all answers`}
              </Button>
              <Spacer />
              <Button
                hidden={isClosed || isDraft || isSubmitted}
                colorScheme="blue"
                isLoading={isSubmitResponseLoading}
                type="submit"
              >{t`Submit my response`}</Button>
            </ButtonGroup>
          </Stack>
        </form>
      )}
    </Container>
  );
};

function hasNonEmptyAnswers(
  response: RespondForm_Response$data | null,
): boolean {
  if (!response || !response.answers.length) {
    return false;
  }

  return !!response.answers.find((answer) => {
    switch (answer.value?.__typename) {
      case 'CustomValue__Checkboxes':
        return !!answer.value.checkboxes?.length;
      case 'CustomValue__Date':
        return !!answer.value.date;
      case 'CustomValue__DateTime':
        return !!answer.value.dateTime;
      case 'CustomValue__MultipleChoice':
        return !!answer.value.multipleChoice;
      case 'CustomValue__Numeric':
        return !!answer.value.number;
      case 'CustomValue__PhoneNumber':
        return !!answer.value.phoneNumber;
      case 'CustomValue__String':
        return !!answer.value.text;
      case 'CustomValue__Time':
        return !!answer.value.time;
      case 'CustomValue__Link':
        return !!answer.value.link;
      default:
        return false;
    }
  });
}
